import React, { useState, useEffect, useRef } from 'react';
import { Form, InputGroup, Dropdown, Badge, Spinner } from 'react-bootstrap';
import { Search, BoxSeam, Person, Laptop } from 'react-bootstrap-icons';
import axios from 'axios';
import './GlobalSearch.css';

const apiUrl = "/api";

function GlobalSearch() {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState({ inventory: [], stock: [], staff: [] });
    const [loading, setLoading] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const searchRef = useRef(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setShowResults(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearch = async (searchQuery) => {
        if (searchQuery.length < 2) {
            setResults({ inventory: [], stock: [], staff: [] });
            setShowResults(false);
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenantId = parseInt(localStorage.getItem('selectedTenant')) || 1;
            const headers = { Authorization: `Bearer ${token}` };

            const [invRes, stockRes, staffRes] = await Promise.all([
                axios.get(`${apiUrl}/inventory?tenant_id=${tenantId}&limit=5`, { headers }).catch(() => ({ data: { data: [] } })),
                axios.get(`${apiUrl}/stock?tenant_id=${tenantId}&limit=5`, { headers }).catch(() => ({ data: { data: [] } })),
                axios.get(`${apiUrl}/staff?tenant_id=${tenantId}`, { headers }).catch(() => ({ data: [] }))
            ]);

            const q = searchQuery.toLowerCase();
            const inventory = (invRes.data.data || []).filter(i =>
                i.SerialNumber?.toLowerCase().includes(q) ||
                i.Type?.toLowerCase().includes(q) ||
                i.Manufacturer?.toLowerCase().includes(q)
            ).slice(0, 3);

            const stock = (stockRes.data.data || []).filter(s =>
                s.name?.toLowerCase().includes(q) ||
                s.category?.toLowerCase().includes(q)
            ).slice(0, 3);

            const staff = (staffRes.data || []).filter(s =>
                s.Name?.toLowerCase().includes(q) ||
                s.Email?.toLowerCase().includes(q)
            ).slice(0, 3);

            setResults({ inventory, stock, staff });
            setShowResults(true);
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setLoading(false);
        }
    };

    const onInputChange = (e) => {
        const value = e.target.value;
        setQuery(value);

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => handleSearch(value), 300);
    };

    const totalResults = results.inventory.length + results.stock.length + results.staff.length;

    return (
        <div className="global-search" ref={searchRef}>
            <InputGroup>
                <InputGroup.Text className="search-icon">
                    {loading ? <Spinner animation="border" size="sm" /> : <Search />}
                </InputGroup.Text>
                <Form.Control
                    type="text"
                    placeholder="Search inventory, stock, staff..."
                    value={query}
                    onChange={onInputChange}
                    onFocus={() => query.length >= 2 && setShowResults(true)}
                    className="search-input"
                />
            </InputGroup>

            {showResults && query.length >= 2 && (
                <div className="search-results">
                    {totalResults === 0 && !loading ? (
                        <div className="no-results">No results found</div>
                    ) : (
                        <>
                            {results.inventory.length > 0 && (
                                <div className="result-section">
                                    <div className="section-header"><Laptop className="me-2" />Inventory</div>
                                    {results.inventory.map((item, i) => (
                                        <a key={i} href={`/#/inventory`} className="result-item" onClick={() => setShowResults(false)}>
                                            <strong>{item.Type}</strong>
                                            <span className="text-muted ms-2">{item.SerialNumber}</span>
                                        </a>
                                    ))}
                                </div>
                            )}
                            {results.stock.length > 0 && (
                                <div className="result-section">
                                    <div className="section-header"><BoxSeam className="me-2" />Stock</div>
                                    {results.stock.map((item, i) => (
                                        <a key={i} href={`/#/stock`} className="result-item" onClick={() => setShowResults(false)}>
                                            <strong>{item.name}</strong>
                                            <Badge bg={item.quantity <= item.min_stock ? 'danger' : 'success'} className="ms-2">{item.quantity}</Badge>
                                        </a>
                                    ))}
                                </div>
                            )}
                            {results.staff.length > 0 && (
                                <div className="result-section">
                                    <div className="section-header"><Person className="me-2" />Staff</div>
                                    {results.staff.map((item, i) => (
                                        <a key={i} href={`/#/addagent`} className="result-item" onClick={() => setShowResults(false)}>
                                            <strong>{item.Name}</strong>
                                            <span className="text-muted ms-2">{item.Email}</span>
                                        </a>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default GlobalSearch;
